import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CALCULATOR_ANKETA_TYPE_LABELS,
  isCalculatorAnketaType,
  type CalculatorAnketaType,
} from '@pomoshnik/shared';
import { listProjectsBySyncStatus } from '@/lib/offline/idb/projects';
import {
  CalculatorEntryView,
  type CalculatorDraftEntry,
  type CalculatorDraftGroup,
} from './CalculatorEntryView';

/**
 * Container для Entry Screen 2.12.1 «Калькулятор» (PRD Block 12).
 *
 * Responsibilities (PR-H3 / ADR-0028):
 *   - Fetch ВСЕ local draft projects из IDB (`listProjectsBySyncStatus('local')`)
 *     и group по `furnitureType` (anketaType-key) для drafts list secktion.
 *   - Navigation:
 *     - TypeCard click → `/calculator/anketa/{anketaType}?new=true` (blank-by-default,
 *       ADR-0028 §Решение #1 — explicit «новый проект»).
 *     - КП click → `/calculator/quote` (Block 12 quote route).
 *     - Draft item click → `/calculator/anketa/{type}?draftId={id}` (resume existing).
 *   - Feedback — default BottomBar flow (navigate('/feedback', { state })).
 *
 * PR-H3 заменил Phase A6 single-draft API path (`apiClient.calculator.listProjects`
 * с IDB fallback) на single-path IDB-only load для drafts list. Online sync остаётся
 * за background-sync layer; UI читает always local state of truth.
 *
 * Container/Presenter split (ADR-0005) — обоснован async IDB fetch с graceful
 * fallback (empty list при failure).
 */
export function CalculatorEntry() {
  const navigate = useNavigate();
  const [draftsByType, setDraftsByType] = useState<CalculatorDraftGroup[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const projects = await listProjectsBySyncStatus('local');
        if (cancelled) return;

        // Group projects by anketaType (furnitureType field).
        // Skip records с malformed furnitureType (defensive: future schema drift).
        const grouped = new Map<CalculatorAnketaType, CalculatorDraftEntry[]>();
        for (const p of projects) {
          if (!isCalculatorAnketaType(p.furnitureType)) continue;
          const arr = grouped.get(p.furnitureType) ?? [];
          arr.push({
            id: p.id,
            name: p.name,
            anketaType: p.furnitureType,
            updatedAt: p.updatedAt,
          });
          grouped.set(p.furnitureType, arr);
        }

        // Build groups, sort drafts within group DESC by updatedAt, sort groups
        // by most-recent-draft updatedAt (recently-touched type на верху).
        const groups: CalculatorDraftGroup[] = [];
        for (const [type, items] of grouped.entries()) {
          const sortedItems = items.slice().sort((a, b) => b.updatedAt - a.updatedAt);
          groups.push({
            anketaType: type,
            typeLabel: CALCULATOR_ANKETA_TYPE_LABELS[type],
            drafts: sortedItems,
          });
        }
        groups.sort((a, b) => (b.drafts[0]?.updatedAt ?? 0) - (a.drafts[0]?.updatedAt ?? 0));

        setDraftsByType(groups);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[CalculatorEntry] loadDrafts failed', err);
        }
        // Leave [] — drafts section не рендерится (graceful degradation).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // useCallback — stable identity для memo на CalculatorEntryView
  // (re-render Container'а на setDraftsByType не должен пересоздавать handlers).

  // PR-H3 (ADR-0028 §Решение #1): tile click = explicit «новый проект» через ?new=true.
  // AnketaFormContainer (Sub-phase 5) увидит этот flag и пропустит Priority 2/3 hydration.
  const handleSelectType = useCallback(
    (anketaType: CalculatorAnketaType) => {
      navigate(`/calculator/anketa/${anketaType}?new=true`);
    },
    [navigate],
  );
  // PR-H5a (ADR-0031): КП-кнопка — явный «открыть КП заново», обнуляет state
  // через `?new=true` (как Home picker «Создать новый»). Bare /calculator/quote
  // (legacy bookmark) сохраняет Priority 2.5 savedQuote fallback.
  const handleOpenQuote = useCallback(() => navigate('/calculator/quote?new=true'), [navigate]);
  // PR-H3 (ADR-0028 §Решение #2): resume specific draft via ?draftId= (existing flow).
  // anketaType приходит из callback args (не closure'а single draft), чтобы поддержать
  // multiple groups + multiple drafts per group.
  const handleResumeDraft = useCallback(
    (draftId: string, anketaType: CalculatorAnketaType) => {
      navigate(`/calculator/anketa/${anketaType}?draftId=${draftId}`);
    },
    [navigate],
  );
  // onFeedback намеренно НЕ передаётся: BottomBar использует default-navigate
  // flow (ADR-0018 §Q9) → navigate('/feedback', { state }) с context из pathInference.

  return (
    <CalculatorEntryView
      draftsByType={draftsByType}
      onSelectType={handleSelectType}
      onOpenQuote={handleOpenQuote}
      onResumeDraft={handleResumeDraft}
    />
  );
}
